/**
 * Application_Form — application-stage pure logic
 * (member-onboarding-flow spec, task 3.1).
 *
 * This module holds the I/O-free gate that decides which step of the
 * Application_Surface renders: the built-in Division_Selection_Entry, the
 * director-built Phase_1_Forms, or nothing at all when the intake window is
 * closed. Like the other pure cores in this directory (`onboardingRouting.ts`,
 * `seasonSelection.ts`, `phaseGating.ts`), the decision is a *total function*
 * of derived state — there is no AWS SDK and no I/O here, only the gating
 * rules — so the same logic runs on the `application-form-service` Lambda
 * (Jest + fast-check) and on the frontend (Vitest + fast-check) as a
 * byte-identical shared module.
 *
 * What lives here:
 *   - `ApplicationStageState` — the derived inputs the gate reads: whether the
 *     target season's intake window is open now, and the member's currently
 *     selected division for that season (or `null`).
 *   - `ApplicationStage` — the discriminated union of the stages the surface
 *     can be in (`DIVISION_ENTRY` | `PHASE_1` | `CLOSED`).
 *   - `resolveApplicationStage(state)` — the total gating function
 *     (Req 4.1, 4.3, 4.4, 4.5).
 *   - `validateDivisionEntryCompletion(selection, divisions)` — the exactly-one
 *     completion rule for the built-in Division_Selection_Entry: it accepts iff
 *     the attempted selection is exactly one division that is active and
 *     application-selectable (reusing `validateDivisionSelection`), and rejects
 *     zero, more-than-one, or an inactive/unknown division so the surface stays
 *     in `DIVISION_ENTRY` and renders no Phase_1_Forms (Req 4.7).
 *
 * Validates: Requirements 4.1, 4.3, 4.4, 4.5, 4.7
 */

import type { Division } from '../division-registry/registryLogic';
import { validateDivisionSelection } from './boundField';

// ---------------------------------------------------------------------------
// Stage state

/**
 * The derived state the application-stage gate reads. Each field is computed by
 * the caller from the Season SSOT and the member's Participation — this module
 * never performs I/O, it only interprets the derived state.
 */
export interface ApplicationStageState {
  /**
   * Whether the target season's intake window is open now, as derived by the
   * caller from `isIntakeOpen(season, now)`. When false the Application_Surface
   * presents no Phase_1_Forms (Req 4.5).
   */
  intakeOpen: boolean;
  /**
   * The member's currently selected division for this season, or `null` when
   * the Division_Selection_Entry has not yet been completed. An empty string is
   * treated the same as `null` (no division selected).
   */
  selectedDivisionKey: string | null;
}

// ---------------------------------------------------------------------------
// Stages

/**
 * Which step of the Application_Surface should render:
 *
 *   - `DIVISION_ENTRY` — intake open, no division selected yet; the built-in
 *     Division_Selection_Entry renders and no Phase_1_Forms are shown
 *     (Req 4.1, 4.3).
 *   - `PHASE_1`        — intake open and a division is selected; the
 *     director-built Phase_1_Forms for that division are available. Carries the
 *     selected `divisionKey` so the caller need not re-derive it (Req 4.4).
 *   - `CLOSED`         — the intake window is not open; no Phase_1_Forms render
 *     (Req 4.5).
 */
export type ApplicationStage =
  | { stage: 'DIVISION_ENTRY' }
  | { stage: 'PHASE_1'; divisionKey: string }
  | { stage: 'CLOSED' };

// ---------------------------------------------------------------------------
// Stage decision

/**
 * The Application_Surface stage, derived as a *total function* of the member's
 * selection state and the season's window. The rules, evaluated in order:
 *
 *   1. Intake window not open → `CLOSED`: no Phase_1_Forms render (Req 4.5).
 *   2. Intake open, no division selected → `DIVISION_ENTRY`: the built-in
 *      division picker renders and no Phase_1_Forms are shown (Req 4.1, 4.3).
 *      A `null` or empty `selectedDivisionKey` both count as "no division".
 *   3. Intake open, a division selected → `PHASE_1(divisionKey)`: the
 *      division-scoped Phase_1_Forms are available (Req 4.4).
 *
 * Pure and total: every `ApplicationStageState` maps to exactly one
 * `ApplicationStage`, and the input is never mutated.
 */
export function resolveApplicationStage(state: ApplicationStageState): ApplicationStage {
  // 1. A closed intake window never exposes Phase_1_Forms (Req 4.5).
  if (!state.intakeOpen) {
    return { stage: 'CLOSED' };
  }

  // 2. Window open but no division chosen yet → the built-in entry (Req 4.1,
  //    4.3). Treat an empty string the same as `null`.
  if (state.selectedDivisionKey == null || state.selectedDivisionKey === '') {
    return { stage: 'DIVISION_ENTRY' };
  }

  // 3. Window open and a division is selected → the Phase_1_Forms (Req 4.4).
  return { stage: 'PHASE_1', divisionKey: state.selectedDivisionKey };
}

// ---------------------------------------------------------------------------
// Division_Selection_Entry completion (Req 4.7)

/**
 * Why a Division_Selection_Entry completion was rejected:
 *
 *   - `NO_DIVISION_SELECTED`       — zero divisions were selected;
 *   - `MULTIPLE_DIVISIONS_SELECTED` — more than one division was selected;
 *   - `INACTIVE_DIVISION`          — the single selected division is unknown,
 *     inactive, or not application-selectable for this season.
 *
 * Every rejection keeps the surface in `DIVISION_ENTRY` and renders no
 * Phase_1_Forms; the accompanying `message` is the user-facing explanation
 * (Req 4.7).
 */
export type DivisionEntryRejectionCode =
  | 'NO_DIVISION_SELECTED'
  | 'MULTIPLE_DIVISIONS_SELECTED'
  | 'INACTIVE_DIVISION';

/**
 * The result of attempting to complete the built-in Division_Selection_Entry, a
 * discriminated union in the repo's pure-core convention:
 *
 *   - `{ ok: true; divisionKey }`      — completion accepted; the caller may
 *     commit the selection (`selectSeason` → `selectDivision`) and advance to
 *     `PHASE_1`;
 *   - `{ ok: false; code; message }`   — completion rejected; the surface shows
 *     the message, stays in `DIVISION_ENTRY`, and presents no Phase_1_Forms.
 */
export type DivisionEntryCompletion =
  | { ok: true; divisionKey: string }
  | { ok: false; code: DivisionEntryRejectionCode; message: string };

/**
 * Validate an attempted completion of the built-in Division_Selection_Entry
 * (Req 4.7). The `selection` is the attempted set of division keys (0..n) the
 * member has chosen; `divisions` is the season's configured division set.
 *
 * Completion is accepted **iff** the selection is exactly one division that is
 * active and application-selectable — the per-division check is delegated to
 * `validateDivisionSelection` (the single source of the active ∧
 * application-selectable rule) rather than re-derived here. Every other
 * attempt is rejected and yields no Phase_1_Forms:
 *
 *   1. zero selected                     → `NO_DIVISION_SELECTED`;
 *   2. more than one selected            → `MULTIPLE_DIVISIONS_SELECTED`;
 *   3. exactly one, but not selectable   → `INACTIVE_DIVISION`.
 *
 * Pure and total; the inputs are never mutated.
 */
export function validateDivisionEntryCompletion(
  selection: readonly string[],
  divisions: readonly Division[],
): DivisionEntryCompletion {
  // 1. Exactly-one cardinality is a precondition for acceptance (Req 4.7).
  if (selection.length === 0) {
    return {
      ok: false,
      code: 'NO_DIVISION_SELECTED',
      message: 'Exactly one division must be selected to continue.',
    };
  }
  if (selection.length > 1) {
    return {
      ok: false,
      code: 'MULTIPLE_DIVISIONS_SELECTED',
      message: 'Exactly one division must be selected to continue.',
    };
  }

  // 2. Exactly one selected — reuse the canonical active ∧ application-selectable
  //    rule rather than re-deriving it (Req 4.7).
  const divisionKey = selection[0];
  const rejection = validateDivisionSelection(divisionKey, divisions);
  if (rejection !== null) {
    return { ok: false, code: 'INACTIVE_DIVISION', message: rejection.message };
  }

  return { ok: true, divisionKey };
}

// ---------------------------------------------------------------------------
// Submission-aware applicant state (applicant-application-flow spec, task 1)

/**
 * The application-workflow status value the resolver reads. Mirrors the
 * `SubmissionStatus` enum value set (`DRAFT | SUBMITTED | APPROVED | REJECTED`)
 * as a string union so this pure module stays dependency-light; `null` means the
 * applicant has no application response for the season yet.
 */
export type ApplicationStatusValue = 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'REJECTED';

/**
 * The derived inputs the submission-aware applicant-state resolver reads. All
 * computed by the caller (the `useApplicantApplicationState` hook) from the
 * active/derived season, the published form, and the applicant's own
 * `ApplicationResponse` — this module performs no I/O.
 */
export interface ApplicantAppStateInput {
  /** Whether the target season's intake window is open now. */
  intakeOpen: boolean;
  /** Whether a published Form_Version exists for the season/division. */
  hasPublishedForm: boolean;
  /** The applicant's selected division for the season, or `null`/'' when none. */
  selectedDivisionKey: string | null;
  /** The application's workflow status, or `null` when no response exists yet. */
  applicationStatus: ApplicationStatusValue | null;
  /**
   * True when a director has returned the application for changes (the DRAFT +
   * review-return marker). Only meaningful when `applicationStatus === 'DRAFT'`.
   */
  reviewReturn?: boolean;
}

/**
 * The single applicant-facing Application State the whole surface keys on. The
 * read-only states (`SUBMITTED`/`REJECTED`/`APPROVED`) and the editable states
 * (`DIVISION_ENTRY`/`IN_PROGRESS`/`CHANGES_REQUESTED`) carry the resolved
 * `divisionKey` where one exists. `note`/`approvedTitle` are layered on by the
 * caller from the loaded response; the pure resolver does not read them.
 */
export type ApplicantAppState =
  | { kind: 'NOT_STARTED' }
  | { kind: 'CLOSED' }
  | { kind: 'NOT_AVAILABLE' }
  | { kind: 'DIVISION_ENTRY' }
  | { kind: 'IN_PROGRESS'; divisionKey: string }
  | { kind: 'CHANGES_REQUESTED'; divisionKey: string }
  | { kind: 'SUBMITTED'; divisionKey: string }
  | { kind: 'REJECTED'; divisionKey: string }
  | { kind: 'APPROVED'; divisionKey: string };

/**
 * Resolve the applicant-facing Application State as a *total function* of the
 * derived inputs (applicant-application-flow Req 3, 6, 7, 8, 9, 10). Precedence
 * (submission awareness first, so an already-decided application is never
 * re-shown as an editable form or as CLOSED even after the window closes):
 *
 *   1. APPROVED                         → `APPROVED` (read-only)
 *   2. SUBMITTED                        → `SUBMITTED` (read-only, under review)
 *   3. DRAFT + review-return marker     → `CHANGES_REQUESTED` (editable again)
 *   4. REJECTED                         → `REJECTED` (read-only, terminal)
 *   5. otherwise (DRAFT without return, or no response):
 *        a. no published form            → `NOT_AVAILABLE`
 *        b. intake window closed         → `CLOSED`
 *        c. no division + no response    → `NOT_STARTED`
 *        d. no division + a DRAFT begun  → `DIVISION_ENTRY`
 *        e. a division selected          → `IN_PROGRESS(divisionKey)`
 *
 * Pure and total: every input maps to exactly one state; the input is never
 * mutated.
 */
export function resolveApplicantAppState(input: ApplicantAppStateInput): ApplicantAppState {
  const divisionKey =
    input.selectedDivisionKey != null && input.selectedDivisionKey !== ''
      ? input.selectedDivisionKey
      : '';

  // 1–2. Decided/submitted applications are read-only regardless of window.
  if (input.applicationStatus === 'APPROVED') {
    return { kind: 'APPROVED', divisionKey };
  }
  if (input.applicationStatus === 'SUBMITTED') {
    return { kind: 'SUBMITTED', divisionKey };
  }
  // 3. A returned DRAFT is editable again (precedence over the window gate so a
  //    late return can still be addressed).
  if (input.applicationStatus === 'DRAFT' && input.reviewReturn === true) {
    return { kind: 'CHANGES_REQUESTED', divisionKey };
  }
  // 4. Rejected is terminal and read-only.
  if (input.applicationStatus === 'REJECTED') {
    return { kind: 'REJECTED', divisionKey };
  }

  // 5. DRAFT (no return) or no response yet — the editable/entry tail.
  if (!input.hasPublishedForm) {
    return { kind: 'NOT_AVAILABLE' };
  }
  if (!input.intakeOpen) {
    return { kind: 'CLOSED' };
  }
  // No division chosen yet: a brand-new applicant (no response) sees the
  // NOT_STARTED entry point; one who has begun (a DRAFT response exists) sees
  // the division picker.
  if (divisionKey === '') {
    return input.applicationStatus == null
      ? { kind: 'NOT_STARTED' }
      : { kind: 'DIVISION_ENTRY' };
  }
  // A division is selected — the applicant is in the editable form, whether or
  // not a draft response has been persisted yet.
  return { kind: 'IN_PROGRESS', divisionKey };
}
